/*
 * VigorX — open the Releasit COD form OVER the PDP, with the Vitals tier
 * discount and free gift already in the cart.
 *
 * DIVISION OF LABOUR
 *   Vitals owns the add. It intercepts add-to-cart at document capture level,
 *   preventDefault + stopPropagation, adds the product AND the qualifying gift
 *   itself, then navigates to /cart. Anything that does its own
 *   fetch("/cart/add.js") bypasses Vitals and silently loses the gift, so this
 *   file never adds anything itself -- it fires a REAL click on the theme's
 *   add-to-cart button and lets Vitals take over.
 *
 *   Vitals' navigation is cancelled through two merchant hooks registered by
 *   the inline wrapper in layout/theme.liquid (it has to be inline and above
 *   the app embed; a deferred file can be beaten by the first add):
 *     VITALS_HOOK__ON_ATC_STAY_ON_THE_SAME_PAGE  runs instead of the navigation
 *     VITALS_HOOK__OPEN_CART_DRAWER              truthy return cancels "go to /cart"
 *   Those hooks relay into onVitalsAdded / onOpenCartDrawer below, buffering
 *   through window.vxCodQueue if Vitals adds before this file has parsed.
 *
 *   Releasit is opened with its own /cart-page routine: derive the Storefront
 *   cart id from the AJAX cart token, READ the cart, push it into _rsiV2 state.
 *   Its clearCart+addProduct rebuild is deliberately NOT used -- that path is
 *   for the cart drawer and concurrent calls raise "Error removing cart lines."
 *
 * DEBUG
 *   window.vxCod.state()
 */
(function () {
  'use strict';

  if (window.vxCod) return;

  var TRIGGER_SELECTOR = [
    '#rsi_buy_now_button', '.rsi_buy_now_button',
    '._rsi-buy-now-button', '.rsi-buy-now-button',
    '#buttonContainerProduct', '#floatingBuyNowButton',
    '[data-vx-buy-trigger]'
  ].join(', ');

  /* CTAs written into the product descriptions. Their in-page anchor target
     (#product-form-template--NNN) belongs to a retired template and no longer
     renders, so the default jump does nothing and every one read as a dead
     click. They scroll the shopper up to the buy form -- they must NOT open
     the COD form themselves. */
  var SCROLL_CTA_SELECTOR = [
    /* Every in-description CTA carries .fnx-scroll-to-shopify. Its only handler
       lived in sections/pf-c0a6e9ea.liquid, a retired PageFly demo section that
       does not render on product templates -- so on 30 of the 32 products these
       62 buttons had no handler AND no href, and did nothing at all. Matching the
       shared class here covers all of them at once; the per-product classes below
       stay for the few descriptions that do not carry it. */
    '.fnx-scroll-to-shopify',
    '.fnx-cta', '.fnx-c100-cta', '.fnx-cen-cta', '.fnx-skg-cta',
    '.vxblk-cta', '.vxbom-cta', '.vxcob-cta', '.vxg-cta', '.vxgel-cta',
    '.vxhon-cta', '.vxlov-cta', '.vxred-cta', '.vxsum-cta', '.vxsup-cta',
    '.vxvid-cta'
  ].join(', ');
  var RELEASIT_OWN_UI = '#cart-wrapper, ._rsi_main_form_element, .rsi-form-main';
  var ATC_FORM_SELECTOR = 'form[action*="/cart/add"]';
  var ATC_BUTTON_SELECTOR =
    'button[name="add"], .product-form__submit, .quick-add__submit';
  var PRODUCT_SCOPE_SELECTOR = [
    'product-info', 'product-card', 'quick-add-modal', '.card-wrapper',
    '.product-card-wrapper', '.quick-add', '.featured-product', '.product',
    'li.grid__item', '[data-product-id]'
  ].join(', ');

  var POLL_INTERVAL_MS = 200;
  var ADD_TIMEOUT_MS = 20000;
  var RUNTIME_TIMEOUT_MS = 12000;
  var FALLBACK_REVEAL_MS = 3500;

  // 'buy'  -> the shopper asked for the COD form
  // 'add'  -> a plain add to cart; stay on the page and just refresh the badge
  var intent = null;
  var driving = false;      // we are the ones clicking the theme's ATC button
  var opening = null;       // single-flight guard for the form opener
  var lastError = null;

  function path() {
    return window.location.pathname.replace(/\/+$/, '') || '/';
  }

  function isCartPage() {
    return path() === '/cart' || path().indexOf('/cart/') === 0;
  }

  function isProductPage() {
    return path().indexOf('/products/') === 0;
  }

  /* ------------------------------------------------------------------ *
   * Cart reads
   * ------------------------------------------------------------------ */

  function fetchCartJs() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('cart.js returned HTTP ' + response.status);
      return response.json();
    });
  }

  /* Lines stamped by the retired Vitals free-gift offer. vx-gift-engine.js used to
     reconcile these on every cart mutation; it was unhooked when the promo was
     pulled, which left orphans reachable in carts saved before that. Nothing in the
     theme patches fetch any more, so these calls cannot re-enter this function. */
  function isRetiredGiftLine(item) {
    var props = (item && item.properties) || {};
    if (props._vdGiftUnitQty !== undefined) return true;
    return typeof props._oid === 'string' && props._oid.indexOf('_vdBonusItem') !== -1;
  }

  // speed 2026-10-14: a complete /cart.js payload (items + token + total)
  function isFullCart(c) {
    return !!(c && Array.isArray(c.items) && typeof c.token === 'string' && typeof c.total_price === 'number');
  }

  // speed 2026-10-14: the buy flow hands over the cart it read right after the add, so the
  // orphan check needs no extra /cart.js round trip (a fresh read only when none is given).
  function pruneRetiredGiftLines(knownCart) {
    var read = isFullCart(knownCart) ? Promise.resolve(knownCart) : fetchCartJs();
    return read.then(function (cart) {
      var orphans = (cart.items || []).filter(isRetiredGiftLine);
      if (!orphans.length) return 0;
      return orphans.reduce(function (chain, line) {
        return chain.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          });
        });
      }, Promise.resolve()).then(function () { return orphans.length; });
    });
  }

  function variantQuantity(cart, variantId) {
    if (!variantId) return 0;
    return (cart.items || []).reduce(function (sum, item) {
      return String(item.variant_id) === String(variantId)
        ? sum + Number(item.quantity || 0)
        : sum;
    }, 0);
  }

  // Vitals stops propagation, so the theme's own add-to-cart event may never
  // fire. Polling the real cart is the only trustworthy confirmation.
  function pollUntilCartGrows(before, variantId) {
    var deadline = Date.now() + ADD_TIMEOUT_MS;
    var baseCount = Number(before.item_count || 0);
    var baseVariant = variantQuantity(before, variantId);

    return new Promise(function (resolve, reject) {
      (function tick() {
        fetchCartJs().then(function (cart) {
          var grew = Number(cart.item_count || 0) > baseCount ||
            variantQuantity(cart, variantId) > baseVariant;
          if (grew) return resolve(cart);
          if (Date.now() > deadline) {
            return reject(new Error('Timed out waiting for the cart to update'));
          }
          window.setTimeout(tick, POLL_INTERVAL_MS);
        }).catch(function (error) {
          if (Date.now() > deadline) return reject(error);
          window.setTimeout(tick, POLL_INTERVAL_MS);
        });
      })();
    });
  }

  /* ------------------------------------------------------------------ *
   * Releasit: the cart-page routine, which is the one that works
   * ------------------------------------------------------------------ */

  function whenReleasitReady() {
    var deadline = Date.now() + RUNTIME_TIMEOUT_MS;
    return new Promise(function (resolve, reject) {
      (function tick() {
        var runtime = window._rsiV2;
        if (runtime && runtime.cartService &&
            typeof runtime.cartService.getCart === 'function' &&
            typeof runtime.setState === 'function') {
          return resolve(runtime);
        }
        if (Date.now() > deadline) {
          return reject(new Error('Releasit runtime never became available'));
        }
        window.setTimeout(tick, 120);
      })();
    });
  }

  // Releasit only accepts a Storefront cart id derived from an AJAX cart token
  // that carries its "?key=" segment; a bare token is not addressable.
  function storefrontCartId(cart) {
    var token = cart && cart.token;
    if (typeof token !== 'string' || token.indexOf('?key=') === -1) return null;
    return 'gid://shopify/Cart/' + token;
  }

  function numericId(gid) {
    if (typeof gid !== 'string') return undefined;
    var tail = gid.split('/').pop();
    var value = Number(tail);
    return isFinite(value) ? value : undefined;
  }

  function alreadyOpen() {
    try {
      var state = window._rsiV2 && window._rsiV2.getState && window._rsiV2.getState();
      return !!(state && state.isOpen);
    } catch (error) {
      return false;
    }
  }

  /* speed 2026-10-14: check the snapshot Releasit actually loads against the settled /cart.js
     (same total and same unit count); if they disagree the cart is still moving -- wait and
     ask Releasit again (max 2 retries), then go on with the latest answer as before. */
  function releasitCartMatches(rcart, ajax) {
    if (!rcart || !isFullCart(ajax)) return true;
    var amount = rcart.cost && rcart.cost.totalAmount && Number(rcart.cost.totalAmount.amount);
    var units = ((rcart.lines && rcart.lines.edges) || []).reduce(function (n, e) { return n + Number(e.node.quantity || 0); }, 0);
    if (!isFinite(amount)) return true;
    return Math.round(amount * 100) === Number(ajax.total_price) && units === Number(ajax.item_count);
  }

  function getCartChecked(runtime, cartId, ajax, triesLeft) {
    return runtime.cartService.getCart(cartId).then(function (result) {
      var rcart = result && result.data && result.data.cart;
      if (triesLeft > 0 && rcart && !releasitCartMatches(rcart, ajax)) {
        return new Promise(function (r) { setTimeout(r, 250); }).then(fetchCartJs).then(function (fresh) {
          return getCartChecked(runtime, cartId, fresh, triesLeft - 1);
        });
      }
      return result;
    });
  }

  function openCodForm(knownCart) {
    if (alreadyOpen()) return Promise.resolve(true);
    if (opening) return opening;
    if (!isFullCart(knownCart)) knownCart = null;
    var settledAt = 0;

    // The COD form snapshots the cart the moment it opens, so whatever sits in the
    // cart at that instant is what the courier collects on. The free-gift promo is
    // retired and vx-gift-engine.js is unhooked, but gift lines it stamped can still
    // be sitting in carts saved before that (Shopify keeps a cart for 14 days). With
    // no qualifying product left beside them the discount never zeroes them, so they
    // bill at full price. Prune them here, every time, before the snapshot is taken.
    var settled = pruneRetiredGiftLines(knownCart).catch(function () { return null; });
    // v2: the «Kupi 4» gift line must be in place and the cart must read the same
    // twice before Releasit snapshots it (Poland zero-total orders #4235/#4422).
    // speed 2026-10-14: when nothing was pruned, the post-add read counts as the first of
    // those two reads (settle still takes a fresh read and compares).
    if (window.vxGift && typeof window.vxGift.settle === 'function') {
      settled = settled.then(function (pruned) { return window.vxGift.settle(pruned === 0 ? knownCart : null); });
    }
    settled = settled.then(function (c) { settledAt = Date.now(); return c; });

    opening = settled.then(function (settledCart) {
      return whenReleasitReady().then(function (runtime) { return { runtime: runtime, cart: settledCart }; });
    }).then(function (ready) {
      var runtime = ready.runtime;
      // speed 2026-10-14: settle's last read IS the latest cart while it is fresh (< 500 ms);
      // otherwise (Releasit was slow to boot, no gift engine, settle failed) read again
      var fresh = isFullCart(ready.cart) && Date.now() - settledAt < 500;
      return (fresh ? Promise.resolve(ready.cart) : fetchCartJs()).then(function (cart) {
        var cartId = storefrontCartId(cart);
        if (!cartId) throw new Error('No addressable Storefront cart id yet');
        if (!(cart.items || []).length) throw new Error('Cart is empty');
        return getCartChecked(runtime, cartId, cart, 2);
      }).then(function (result) {
        var cart = result && result.data && result.data.cart;
        if (!cart) throw new Error('Releasit returned no cart');

        var edges = (cart.lines && cart.lines.edges) || [];
        var products = edges.map(function (edge) { return edge.node.merchandise; });
        // Releasit treats «current variant» as the product of the page: once its cart goes empty it
        // re-adds that variant × the page quantity. It must never be the gift (Kenan 2026-10-14:
        // deleting the product in the form put 4 × gift at full price back into the cart).
        var giftGid = window.vxGift && window.vxGift.rule ? 'ProductVariant/' + window.vxGift.rule.variantId : ' ';
        var first = products.filter(function (m) { return String(m.id).indexOf(giftGid) === -1; })[0] || {};

        // A pure READ pushed into state. No clearCart, no addProduct.
        runtime.setState({
          cartInfo: cart,
          products: products,
          currentVariantId: numericId(first.id),
          currentProductId: numericId(first.product && first.product.id),
          triggerCartUpdate: 0,
          isOpen: true,
          isCartButtonLoading: false,
          isClearingCart: false
        });
        return true;
      });
    }).catch(function (error) {
      lastError = error;
      throw error;
    }).finally(function () {
      opening = null;
    });

    return opening;
  }

  /* ------------------------------------------------------------------ *
   * Staying on the page: badge + cart-page re-render
   *
   * The hooks cancel the reload Vitals used to do, so on /cart the lines and
   * totals have to be repainted here or the claimed gift silently never shows.
   * ------------------------------------------------------------------ */

  function sectionInnerHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var node = doc.querySelector(selector);
    return node ? node.innerHTML : null;
  }

  // The header renders the count bubble inline rather than as its own section,
  // so the sections API cannot return it on a product page -- asking for it came
  // back empty and the badge silently went stale after an add. Driving it from
  // /cart.js instead is both simpler and always right. The visually-hidden label
  // keeps its translated wording; only the number inside it changes.
  function updateCartBadge(count) {
    var link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    var bubble = link.querySelector('.cart-count-bubble');

    if (!count) {
      if (bubble) bubble.remove();
      return;
    }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      link.appendChild(bubble);
    }
    var visible = bubble.querySelector('[aria-hidden="true"]');
    if (visible) visible.textContent = count < 100 ? count : '';
    var hidden = bubble.querySelector('.visually-hidden');
    if (hidden) {
      hidden.textContent = /\d/.test(hidden.textContent)
        ? hidden.textContent.replace(/\d+/, count)
        : String(count);
    }
  }

  function refreshCartUi() {
    var cartPage = isCartPage();

    var badge = fetchCartJs().then(function (cart) {
      var giftId = window.vxGift && window.vxGift.rule && window.vxGift.rule.variantId;
      var visible = (cart.items || []).reduce(function (n, i) { return i.variant_id === giftId ? n : n + i.quantity; }, 0);
      updateCartBadge(visible);
      return cart;
    });
    if (!cartPage) return badge.catch(function (error) { lastError = error; });

    // Real section ids come from the DOM (JSON templates name them template--…__cart-items);
    // asking for «main-cart-items» returned nothing, so the page was never repainted and the
    // next stepper tap used stale line numbers («Parametar line nije valjan»).
    var ids = ['main-cart-items', 'main-cart-footer'].map(function (id) {
      var el = document.getElementById(id); return el && el.dataset.id ? el.dataset.id : null;
    });
    if (!ids[0]) return badge.catch(function (error) { lastError = error; });
    // No «Accept: application/json» here: with it Shopify answers /cart with the CART json, not
    // the sections, and nothing ever repainted. In theme preview, ask for the previewed theme.
    var preview = (window.Shopify && Shopify.theme && Shopify.theme.role !== 'main' && Shopify.theme.id) ? '&preview_theme_id=' + Shopify.theme.id : '';
    return fetch(path() + '?sections=' + ids.filter(Boolean).join(',') + preview, {
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('sections returned HTTP ' + response.status);
      return response.json();
    }).then(function (sections) {
      ['main-cart-items', 'main-cart-footer'].forEach(function (id, k) {
        var sid = ids[k];
        if (!sid || !sections[sid]) return;
        var host = document.getElementById(id);
        if (!host) return;
        var target = host.querySelector('.js-contents') || host;
        var selector = host.querySelector('.js-contents') ? '.js-contents' : '#' + id;
        var markup = sectionInnerHTML(sections[sid], selector);
        if (markup !== null) target.innerHTML = markup;
      });

      return fetchCartJs().then(function (cart) {
        var wrapper = document.querySelector('.cart, cart-items, #main-cart-items');
        if (wrapper) wrapper.classList.toggle('is-empty', Number(cart.item_count || 0) === 0);
        return cart;
      });
    }).catch(function (error) {
      lastError = error;
    });
  }

  /* ------------------------------------------------------------------ *
   * Hooks relayed from the inline wrapper
   * ------------------------------------------------------------------ */

  function settle() {
    if (intent === 'buy') {
      intent = null;
      return openCodForm().catch(function () { return refreshCartUi(); });
    }
    intent = null;
    return refreshCartUi();
  }

  // Runs INSTEAD of Vitals' post-add navigation.
  function onVitalsAdded() {
    return settle();
  }

  // Truthy return cancels Vitals' "go to /cart".
  function onOpenCartDrawer() {
    settle();
    return true;
  }

  /* ------------------------------------------------------------------ *
   * Click bridge
   * ------------------------------------------------------------------ */

  function scopedForm(trigger) {
    var form = trigger.closest(ATC_FORM_SELECTOR);
    if (form) return form;

    var scope = trigger.closest(PRODUCT_SCOPE_SELECTOR);
    while (scope) {
      /* A PDP carries more than one form[action*="/cart/add"]: Shopify's
         payment-installments form has no add button. Taking the first match
         handed startBuy a buttonless form and it bailed out, so require a form
         that actually contains an add-to-cart control. */
      var candidates = scope.querySelectorAll(ATC_FORM_SELECTOR);
      for (var i = 0; i < candidates.length; i += 1) {
        if (candidates[i].querySelector(ATC_BUTTON_SELECTOR)) return candidates[i];
      }
      scope = scope.parentElement && scope.parentElement.closest(PRODUCT_SCOPE_SELECTOR);
    }

    var main = document.querySelector(
      'form[data-type="add-to-cart-form"][action*="/cart/add"]'
    );
    if (main) return main;

    var visible = Array.prototype.filter.call(
      document.querySelectorAll(ATC_FORM_SELECTOR),
      function (candidate) {
        return candidate.offsetParent !== null || candidate.getClientRects().length;
      }
    );
    return visible.length === 1 ? visible[0] : null;
  }

  // Safety net for the case where Vitals is not intercepting after all: this
  // theme has settings.cart_type "page", so Dawn's product-form would hard
  // navigate to /cart on a successful add. Lending it a detached cart makes it
  // take its non-navigating branch instead.
  function silenceThemeNavigation(form) {
    var host = form && form.closest && form.closest('product-form');
    if (!host || host.cart) return function () {};
    var stub = document.createElement('div');
    stub.getSectionsToRender = function () { return []; };
    stub.setActiveElement = function () {};
    stub.renderContents = function () {};
    host.cart = stub;
    return function () { if (host.cart === stub) host.cart = null; };
  }

  function startBuy(trigger) {
    if (driving) return;

    var form = scopedForm(trigger);
    var button = form && form.querySelector(ATC_BUTTON_SELECTOR);
    if (!form || !button || button.disabled) {
      lastError = new Error('No add-to-cart control in scope');
      return;
    }

    intent = 'buy';
    driving = true;
    // The COD form opens over the page; the theme's «Artikl je dodan» popup (fired by the
    // add) is redundant there — keep it suppressed for the whole buy flow (Kenan).
    var root = document.documentElement;
    root.classList.add('vx-buying');
    clearTimeout(window.__vxBuyingT);
    window.__vxBuyingT = setTimeout(function () { root.classList.remove('vx-buying'); }, 10000);
    var cn = document.querySelector('cart-notification');
    var shut = function () { try { if (cn && typeof cn.close === 'function') cn.close(); } catch (e) {} var el = document.getElementById('cart-notification'); if (el) el.classList.remove('active', 'animate'); };
    lastError = null;

    var wasDisabled = trigger.disabled;
    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');
    root.classList.add('vx-cod-busy');
    var restore = silenceThemeNavigation(form);

    function release() {
      driving = false;
      restore();
      root.classList.remove('vx-cod-busy');
      if (trigger.isConnected) {
        trigger.disabled = wasDisabled;
        trigger.removeAttribute('aria-busy');
      }
    }

    var variantId = new FormData(form).get('id');

    // v2: one direct add of the chosen pack (+ the gift when it is earned). The
    // Vitals tier discount is applied server-side by quantity (verified on the
    // live cart: 4 x Kamagra Gold via plain /cart/add.js = 66.68), so nothing
    // depends on Vitals' widget or its click interception any more.
    var qty = (window.vxPacks && window.vxPacks.qty()) ||
              parseInt(new FormData(form).get('quantity'), 10) || 1;
    var items = [{ id: Number(variantId), quantity: qty }];
    var gift = window.vxGift && window.vxGift.rule;
    if (gift && qty >= gift.min) items.push({ id: gift.variantId, quantity: 1 });

    fetchCartJs().then(function (before) {
      var JSONH = { 'Content-Type': 'application/json', Accept: 'application/json' };
      var line = (before.items || []).filter(function (i) { return gift && i.variant_id === gift.variantId; })[0];
      if (line) items = items.filter(function (i) { return i.id !== gift.variantId; }); // one gift per cart
      // «Buy now» means THIS pack: if the product is already in the cart (an earlier visit,
      // a test, a double tap) set its quantity to the chosen pack instead of adding on top
      // (4 left over + a 2-pack used to open the form on 6).
      var existing = (before.items || []).filter(function (i) { return String(i.variant_id) === String(variantId); })[0];
      var step = existing
        ? fetch('/cart/change.js', { method: 'POST', headers: JSONH, body: JSON.stringify({ id: existing.key, quantity: qty }) })
            .then(function (r) {
              if (!r.ok) throw new Error('change failed ' + r.status);
              var rest = items.filter(function (i) { return String(i.id) !== String(variantId); });
              return rest.length ? fetch('/cart/add.js', { method: 'POST', headers: JSONH, body: JSON.stringify({ items: rest }) }) : null;
            })
        : fetch('/cart/add.js', { method: 'POST', headers: JSONH, body: JSON.stringify({ items: items, attributes: { _vx2: '1' } }) })
            .then(function (r) { if (!r.ok) throw new Error('add failed ' + r.status); return pollUntilCartGrows(before, variantId); });
      return step;
    }).then(function (cartAfter) {
      shut();
      // speed 2026-10-14: hand the post-add cart to the opener (a Response from the
      // change.js branch is not a cart, so that branch keeps the original fresh reads)
      return openCodForm(isFullCart(cartAfter) ? cartAfter : null);
    }).then(function () {
      shut();
      intent = null;
      release();
    }).catch(function (error) {
      lastError = error;
      intent = null;
      release();
    });
  }

  function buyFormTarget() {
    return document.querySelector('product-form') ||
           document.querySelector('form[action*="/cart/add"] .product-form__submit') ||
           document.querySelector('.product-form__buttons');
  }

  function onDocumentClick(event) {
    var scrollCta = event.target && event.target.closest
      ? event.target.closest(SCROLL_CTA_SELECTOR)
      : null;
    if (scrollCta && !scrollCta.closest(RELEASIT_OWN_UI) && !isCartPage()) {
      event.preventDefault();
      var dest = buyFormTarget();
      if (dest) {
        try {
          dest.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
          dest.scrollIntoView();
        }
      }
      return;
    }

    var trigger = event.target && event.target.closest
      ? event.target.closest(TRIGGER_SELECTOR)
      : null;

    if (trigger) {
      // Releasit's own form UI must keep working normally.
      if (trigger.closest(RELEASIT_OWN_UI)) return;
      // The cart page already works; leave it entirely alone.
      if (isCartPage()) return;

      // Releasit's handler must never run: it would add the product through its
      // own cart and the gift would be dropped.
      event.preventDefault();
      event.stopImmediatePropagation();
      startBuy(trigger);
      return;
    }

    if (!driving && event.isTrusted &&
        event.target && event.target.closest &&
        event.target.closest(ATC_BUTTON_SELECTOR)) {
      intent = 'add';
    }
  }

  /* ------------------------------------------------------------------ *
   * Theme-owned fallback trigger, revealed only if Releasit never shows up
   * ------------------------------------------------------------------ */

  function installFallbackTrigger() {
    if (!isProductPage()) return;

    var host = document.querySelector('.product-form__buttons');
    if (!host || host.querySelector('[data-vx-buy-trigger]')) return;

    var style = document.createElement('style');
    style.textContent =
      '.vx-cod-fallback{display:none;width:100%;margin-top:1rem;cursor:pointer;' +
      'padding:1.2rem 2rem;border:0;border-radius:8px;background:#FF7500;' +
      'color:#fff;font-size:1.5rem;font-weight:600;line-height:1.2}' +
      '.vx-cod-fallback[data-vx-visible="true"]{display:block}';
    document.head.appendChild(style);

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'vx-cod-fallback';
    button.setAttribute('data-vx-buy-trigger', '');
    button.textContent = 'Zamawiam za pobraniem';
    host.appendChild(button);

    window.setTimeout(function () {
      var releasitShowed = !!document.querySelector(
        '#rsi_buy_now_button, .rsi_buy_now_button, ._rsi-buy-now-button, .rsi-buy-now-button'
      );
      if (!releasitShowed) button.setAttribute('data-vx-visible', 'true');
    }, FALLBACK_REVEAL_MS);
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  /* Re-read the cart INTO the already open form (no quantity changes): the same pure read
     openCodForm does, pushed into Releasit state while the form stays open. Used by the gift
     engine after it adds the gift behind the form (replaces the visible «+ then −» nudge). */
  function reloadForm() {
    return whenReleasitReady().then(function (runtime) {
      return fetchCartJs().then(function (cart) {
        var cartId = storefrontCartId(cart);
        if (!cartId) throw new Error('No addressable Storefront cart id yet');
        return runtime.cartService.getCart(cartId);
      }).then(function (result) {
        var cart = result && result.data && result.data.cart;
        if (!cart) throw new Error('Releasit returned no cart');
        var edges = (cart.lines && cart.lines.edges) || [];
        runtime.setState({
          cartInfo: cart,
          products: edges.map(function (edge) { return edge.node.merchandise; }),
          triggerCartUpdate: Date.now()
        });
        return true;
      });
    }).catch(function (error) { lastError = error; return false; });
  }

  /* «Dodaj u košaricu» on the product page — ours, not Vitals'. The hidden Vitals widget used to
     catch this click and add ITS default tier (2) instead of the chosen pack, and the theme button
     stayed stuck in its loading state. Capture on window (before Vitals' document listener), add the
     chosen pack (+ gift at ≥ min) the same way «Kupujte pouzećem» does, update the badge, say
     «Dodano ✓» on the button. No popup (Kenan). */
  function addChosenPack(form) {
    var variantId = new FormData(form).get('id');
    var qty = (window.vxPacks && window.vxPacks.qty()) || parseInt(new FormData(form).get('quantity'), 10) || 1;
    var gift = window.vxGift && window.vxGift.rule;
    var JSONH = { 'Content-Type': 'application/json', Accept: 'application/json' };
    return fetchCartJs().then(function (before) {
      var items = [{ id: Number(variantId), quantity: qty }];
      var hasGift = (before.items || []).some(function (i) { return gift && i.variant_id === gift.variantId; });
      if (gift && qty >= gift.min && !hasGift) items.push({ id: gift.variantId, quantity: 1 });
      var existing = (before.items || []).filter(function (i) { return String(i.variant_id) === String(variantId); })[0];
      if (existing) {
        return fetch('/cart/change.js', { method: 'POST', headers: JSONH, body: JSON.stringify({ id: existing.key, quantity: qty }) })
          .then(function (r) {
            if (!r.ok) throw new Error('change ' + r.status);
            var rest = items.filter(function (i) { return String(i.id) !== String(variantId); });
            return rest.length ? fetch('/cart/add.js', { method: 'POST', headers: JSONH, body: JSON.stringify({ items: rest }) }) : null;
          });
      }
      return fetch('/cart/add.js', { method: 'POST', headers: JSONH, body: JSON.stringify({ items: items }) })
        .then(function (r) { if (!r.ok) throw new Error('add ' + r.status); });
    }).then(function () {
      return window.vxGift && window.vxGift.settle ? window.vxGift.settle() : null;
    }).then(function () { return refreshCartUi(); });
  }
  window.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest('.product-form__submit');
    if (!btn || !isProductPage() || !document.querySelector('[data-vx-packs]')) return;
    var form = btn.form || btn.closest('form');
    if (!form) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (btn.dataset.vxBusy) return;
    btn.dataset.vxBusy = '1';
    var label = btn.querySelector('span');
    var original = label ? label.innerHTML : '';
    btn.classList.add('vx-adding');
    addChosenPack(form).then(function () {
      if (label) label.innerHTML = '✓ Dodano do koszyka';
      btn.classList.add('vx-added');
    }).catch(function (e) {
      lastError = e;
      if (label) label.textContent = 'Spróbuj ponownie';
    }).then(function () {
      btn.classList.remove('vx-adding');
      setTimeout(function () { if (label) label.innerHTML = original; btn.classList.remove('vx-added'); delete btn.dataset.vxBusy; }, 2200);
    });
  }, true);

  /* Cart page: when the summary repaints (4th pack → gift), Releasit re-injects «Kupujte pouzećem»
     into the first form it finds — the hidden cart-notification — so the button vanished from the
     summary. Keep it in the summary's CTA slot. */
  function homeCodButton() {
    if (!isCartPage()) return;
    var btn = document.getElementById('rsi_buy_now_button');
    var footer = document.getElementById('main-cart-footer');
    if (!btn || !footer || footer.contains(btn)) return;
    // move Releasit's own wrapper (its React root) with the button, into the same place it starts in
    var node = btn.closest('#cart-wrapper') || btn;
    var slot = footer.querySelector('.cart__blocks') || footer;
    slot.appendChild(node);
  }
  if (document.body) {
    new MutationObserver(function () { homeCodButton(); }).observe(document.body, { childList: true, subtree: true });
  }

  /* «Artikl je dodan» popup never sits on/behind the COD form: if it turns active while the
     form is open or a buy is running, close it straight away (Kenan 2026-10-12). */
  (function guardNotification() {
    var el = document.getElementById('cart-notification');
    if (!el || !('MutationObserver' in window)) return;
    new MutationObserver(function () {
      if (!el.classList.contains('active')) return;
      var buying = document.documentElement.classList.contains('vx-buying');
      if (buying || alreadyOpen()) {
        var host = document.querySelector('cart-notification');
        try { if (host && typeof host.close === 'function') host.close(); } catch (e) {}
        el.classList.remove('active', 'animate');
      }
    }).observe(el, { attributes: true, attributeFilter: ['class'] });
  })();

  window.vxCod = {
    reloadForm: reloadForm,
    onVitalsAdded: onVitalsAdded,
    onOpenCartDrawer: onOpenCartDrawer,
    open: openCodForm,
    refresh: refreshCartUi,
    state: function () {
      return {
        intent: intent,
        driving: driving,
        opening: !!opening,
        hooksInstalled: !!window.vxCodHooksInstalled,
        releasitReady: !!(window._rsiV2 && window._rsiV2.cartService),
        lastError: lastError ? String(lastError.message || lastError) : null
      };
    }
  };

  document.addEventListener('click', onDocumentClick, true);

  /* speed 2026-10-14: instant feedback on «Kupujte pouzećem» while the form is prepared */
  (function installBusyStyle() {
    var s = document.createElement('style');
    s.textContent =
      '#rsi_buy_now_button[aria-busy="true"],html.vx-cod-busy .vx-sticky__btn{position:relative;cursor:progress;opacity:.9;padding-right:44px}' +
      '#rsi_buy_now_button[aria-busy="true"]::after,html.vx-cod-busy .vx-sticky__btn::after{content:"";position:absolute;right:14px;top:50%;' +
      'width:18px;height:18px;margin-top:-9px;border:2.5px solid rgba(255,255,255,.5);border-top-color:#fff;border-radius:50%;' +
      'animation:vxCodSpin .7s linear infinite}@keyframes vxCodSpin{to{transform:rotate(360deg)}}';
    document.head.appendChild(s);
  })();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installFallbackTrigger, { once: true });
  } else {
    installFallbackTrigger();
  }
  document.addEventListener('shopify:section:load', installFallbackTrigger);

  // Replay anything the inline wrapper buffered before this file parsed.
  var queued = window.vxCodQueue || [];
  window.vxCodQueue = { push: function (entry) { relay(entry); } };
  queued.forEach(relay);

  function relay(entry) {
    var name = entry && entry[0];
    var args = (entry && entry[1]) || [];
    if (typeof window.vxCod[name] === 'function') {
      try { window.vxCod[name].apply(window.vxCod, args); } catch (error) { lastError = error; }
    }
  }
})();
